import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, LinearProgress, Paper, Snackbar, SnackbarCloseReason, TextField, Typography,
} from '@mui/material';
import { SessaoCaixaService } from '../../../shared/services/api/Financeiro/SessaoCaixa';

export interface DialogFecharSessaoCaixaProps {
  open: boolean;
  idconta: string;
  nomeConta?: string;
  dataabertura?: string;
  onClose: (value?: string) => void;
}

const formatMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Fecha a sessão de caixa do usuário logado (Faturamento), transferindo pro
// Cofre o valor que o operador informar — não precisa ser o saldo real
// inteiro (o backend só recusa transferir MAIS do que existe, ver
// fecharSessao/fecharSessaoResolvida, SessaoCaixa.ts backend). O que não for
// transferido continua na conta do caixa, disponível pra próxima sessão
// (ex.: deixar um fundo de troco). `valorTotal` é o teto (soma de todas as
// formas do saldo real do caixa, todas somadas — não só Dinheiro: PIX/Cartão
// de vendas novas já saem roteados direto pras contas de recebíveis na hora
// da venda, ver ContasRecebiveisFixas.ts backend, mas saldo antigo lançado
// direto no caixa antes desse roteamento existir também precisa ter como sair
// daqui). A conta de destino não é escolhida pelo usuário: o backend sempre
// usa o Cofre, a mesma conta usada na abertura.
export const DialogFecharSessaoCaixa: React.FC<DialogFecharSessaoCaixaProps> = ({
  open, idconta, nomeConta, dataabertura, onClose,
}) => {
  const [carregando, setCarregando] = useState(false);
  const [erroCarregar, setErroCarregar] = useState('');
  // Movimento só desta sessão (o que a tela mostra) — diferente do saldo real
  // all-time usado pra calcular `valorTotal` abaixo, que é o teto contra o
  // qual o backend valida o fechamento (ver comentário de valorTotal).
  const [totaisPorForma, setTotaisPorForma] = useState<{ categoria: string; valor: number }[]>([]);

  // Teto disponível pra transferir — soma de todas as formas do saldo real
  // do caixa, sempre calculado pelo backend (mesma consulta que fecharSessao
  // usa pra validar).
  const [valorTotal, setValorTotal] = useState(0);
  // Valor que o operador de fato quer transferir agora — em centavos, dígitos
  // crus (mesmo padrão de DialogAbrirSessaoCaixa). Pré-preenchido com o teto
  // quando o saldo carrega, mas editável: o operador pode reduzir pra deixar
  // parte do dinheiro no caixa.
  const [valorFechamentoTexto, setValorFechamentoTexto] = useState('');
  const [fechando, setFechando] = useState(false);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  useEffect(() => {
    if (!open || !idconta) return;

    setTotaisPorForma([]);
    setValorTotal(0);
    setValorFechamentoTexto('');
    setErroCarregar('');
    setCarregando(true);

    // `valorTotal` (o que de fato é transferido) precisa continuar vindo do
    // saldo real ALL-TIME — é contra esse número que o backend valida o
    // fechamento (cobre sobra de sessão anterior nunca fechada certinho). O
    // resumo por forma de pagamento EXIBIDO, porém, é só o movimento desta
    // sessão (getFluxoSessao) — evita mostrar o "caixa todo" quando o pedido
    // era só ver o que entrou/saiu desde que essa sessão abriu.
    Promise.all([
      SessaoCaixaService.getSaldoRealSessao(),
      SessaoCaixaService.getFluxoSessao(idconta, dataabertura || ''),
    ]).then(([saldoReal, fluxoSessao]) => {
      setCarregando(false);
      if (saldoReal instanceof Error) {
        setErroCarregar(saldoReal.message);
        return;
      }
      const total = saldoReal.reduce((acc, f) => acc + f.valor, 0);
      setValorTotal(Number(total.toFixed(2)));
      // Pré-preenche com o teto (fecha o valor todo por padrão, mesmo
      // comportamento de antes) — o operador reduz manualmente se quiser
      // deixar parte do saldo no caixa.
      setValorFechamentoTexto(total > 0 ? String(Math.round(total * 100)) : '');

      if (fluxoSessao instanceof Error) {
        // Sem o resumo por sessão, cai pro all-time (melhor mostrar algo do
        // que nada) — mas isso não deveria acontecer nos tenants normais.
        setTotaisPorForma(saldoReal);
        return;
      }
      setTotaisPorForma(fluxoSessao.porFormaPagamento);
    });
  }, [open, idconta, dataabertura]);

  // Mesmo padrão de DialogAbrirSessaoCaixa: texto guarda só dígitos (centavos),
  // valor de verdade é dígitos/100.
  const valorFechamento = valorFechamentoTexto ? Number(valorFechamentoTexto) / 100 : 0;
  const valorFechamentoExibido = valorFechamento === 0
    ? ''
    : valorFechamento.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const handleChangeValorFechamento = (texto: string) => {
    setValorFechamentoTexto(texto.replace(/\D/g, ''));
  };
  // Único limite real: não dá pra transferir mais do que existe no caixa —
  // o backend recusaria (ver fecharSessaoResolvida). Transferir menos é
  // permitido.
  const excedeSaldo = valorFechamento > valorTotal + 0.005;

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') onClose('true');
  };

  const confirmar = () => {
    if (excedeSaldo) {
      setTms('warning');
      setMsg('O valor a transferir não pode ser maior que o saldo disponível no caixa.');
      setOpenmsg(true);
      return;
    }

    // Valor zerado (operador optou por deixar tudo no caixa, sessão sem
    // movimento, ou saldo já zerado) fecha sem transferir nada — manda a
    // lista vazia em vez de uma forma com valor zero (o backend rejeitaria
    // um item com valor <= 0).
    setFechando(true);
    SessaoCaixaService
      .fechar(valorFechamento > 0 ? [{ categoria: 'Dinheiro', valor: valorFechamento }] : [])
      .then((resultado) => {
        setFechando(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        setTms('success');
        setMsg('Sessão fechada com sucesso!');
        setOpenmsg(true);
      });
  };

  const handleCancel = () => onClose('false');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Fechar sessão de caixa{nomeConta ? ` — ${nomeConta}` : ''}</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        {carregando ? (
          <LinearProgress variant="indeterminate" />
        ) : erroCarregar ? (
          <Alert severity="error">{erroCarregar}</Alert>
        ) : (
          <Box component={Paper} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Movimento desta sessão, por forma de pagamento
            </Typography>
            {totaisPorForma.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nenhum movimento nesta sessão.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                {totaisPorForma.map((forma) => (
                  <Box key={forma.categoria} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{forma.categoria}</Typography>
                    <Typography variant="body2" fontWeight={600} color={forma.valor < 0 ? 'error.main' : 'text.primary'}>
                      R$ {formatMoeda(forma.valor)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Saldo disponível no caixa: <strong>R$ {formatMoeda(valorTotal)}</strong> — pode
              ser diferente da soma do movimento desta sessão (acima) se
              houver sobra de uma sessão anterior que não foi transferida.
              Você não precisa transferir tudo: o que não for transferido
              continua no caixa, disponível pra próxima sessão.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Valor a transferir para o Cofre"
              placeholder="0,00"
              value={valorFechamentoExibido}
              onChange={(e) => handleChangeValorFechamento(e.target.value)}
              inputProps={{ inputMode: 'numeric' }}
              error={excedeSaldo}
              helperText={excedeSaldo ? 'Não pode ser maior que o saldo disponível no caixa.' : ' '}
              disabled={fechando}
            />
            {valorTotal <= 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Sem saldo pra transferir — a sessão fecha sem movimentar valor nenhum.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={fechando}>Cancelar</Button>
        {!erroCarregar && (
          <Button variant="outlined" autoFocus disabled={fechando || carregando || excedeSaldo} onClick={confirmar}>
            {fechando ? 'Fechando...' : 'Confirmar e fechar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
